import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select'; // Import react-select
import { rmApi, productApi } from '../../services/api';
import { FaSpinner, FaExclamationTriangle, FaSearch, FaFilter, FaPlus } from 'react-icons/fa';
import Pagination from '../../components/Pagination';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate}, ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

const formatDateExtractMonth = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedMonth = date.toLocaleDateString('en-GB', {
      month: 'long'
    });
    return `${formattedMonth}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function RMPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dsoOptions, setDsoOptions] = useState([]); // Keep original if needed elsewhere
  const [formattedDsoOptions, setFormattedDsoOptions] = useState([]); // State for react-select

  // Filter states
  const [dsoFilter, setDsoFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPayouts, setTotalPayouts] = useState(0);
  const [entriesPerPage] = useState(10);

  // Define options for Status filter
  const statusFilterOptions = [
    { value: '', label: 'All Status' },
    { value: 'requested', label: 'Requested' }, // Using string values for better readability
    { value: 'approved', label: 'Approved' },  
    { value: 'disbursed', label: 'Disbursed' }, 
  ];

  // Load DSOs for filter dropdown
  useEffect(() => {
    const fetchDSOs = async () => {
      try {
        const response = await rmApi.getAssignedDsos({ limit: 100 }); // Fetch a reasonable limit
        const dsos = response.data.items || [];
        setDsoOptions(dsos); // Keep original data if needed elsewhere
        // Format for react-select: { value: dso.id, label: dso.name }
        const options = dsos.map(dso => ({
          value: dso.id, // Assuming 'id' is the unique identifier
          label: dso.name // Assuming 'name' is the display name
        }));
        setFormattedDsoOptions([{ value: '', label: 'All DSOs' }, ...options]); // Add "All" option
      } catch (err) {
        console.error("Error fetching DSOs:", err);
        // Handle error appropriately, maybe set an error state
      }
    };

    fetchDSOs();
    // Add other initial data fetching if needed
  }, []); // Empty dependency array ensures this runs only once on mount


  // Fetch payouts function
  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = (currentPage - 1) * entriesPerPage;
      const params = { 
        skip, 
        limit: entriesPerPage,
        ...(dsoFilter && { dso_id: dsoFilter }),
        ...(statusFilter && { status: statusFilter })
      };
      
      const response = await rmApi.getPayouts(params);
      setPayouts(response.data.items || []);
      setTotalPayouts(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / entriesPerPage));
    } catch (err) {
      console.error("Error fetching payouts:", err);
      setError('Failed to load payouts. Please try again.');
      setPayouts([]);
      setTotalPayouts(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, dsoFilter, statusFilter]);

  // Initial fetch and fetch on filter/page change
  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  // Handle page change from Pagination component
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Filter change handlers
  const handleDsoFilterChange = (selectedOption) => {
    // selectedOption will be null if cleared, or { value: ..., label: ... }
    setDsoFilter(selectedOption ? selectedOption.value : '');
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleStatusFilterChange = (selectedOption) => {
    // selectedOption will be null if cleared (if isClearable is true), or { value: ..., label: ... }
    setStatusFilter(selectedOption ? selectedOption.value : '');
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Reset filters
  const resetFilters = () => {
    setDsoFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  // Function to render status badge
  const renderStatusBadge = (status) => {
    let badgeClass = 'rm-status-badge ';
    switch (status?.toLowerCase()) {
      case 'disbursed':
        badgeClass += 'rm-status-verified'; // Green
        break;
      case 'approved':
        badgeClass += 'rm-status-active'; // Blue
        break;
      case 'requested':
        badgeClass += 'rm-status-pending'; // Yellow/Orange
        break;
      default:
        badgeClass += 'rm-status-unknown'; // Default style
    }
    return <span className={badgeClass}>{status || 'Unknown'}</span>;
  };

  return (
    <div className="rm-payouts-page rm-list-page">
      <div className="rm-list-header">
        <h2>DSO Payouts</h2>
        <button 
          className="rm-create-payout-btn"
          onClick={() => window.location.href = '/rm/payouts/create'}
        >
          <FaPlus className="btn-icon" /> Create Payout
        </button>
      </div>

      {/* Filters */}
      <div className="rm-filters-container">
        <div className="rm-filters-row">
          {/* DSO Filter */}
          <div className="rm-filter">
            <label htmlFor="dsoFilter">DSO:</label>
            <Select
              inputId="dsoFilter" // Use inputId for htmlFor association
              options={formattedDsoOptions}
              value={formattedDsoOptions.find(option => option.value === dsoFilter)} // Find the object matching the current value
              onChange={handleDsoFilterChange}
              isClearable // Allows deselecting to show all
              placeholder="Select DSO..."
              className="rm-react-select-container" // Class for the outer container
              classNamePrefix="rm-react-select" // Prefix for internal elements (e.g., rm-react-select__control)
              styles={{ // Optional: Basic styling override if needed
                container: (base) => ({ ...base, width: '200px', zIndex: 10 }) // Example: Set width and z-index
              }}
            />
          </div>

          {/* Status Filter */}
          <div className="rm-filter">
            <label htmlFor="statusFilter">Status:</label>
            <Select
              inputId="statusFilter"
              options={statusFilterOptions}
              value={statusFilterOptions.find(option => option.value === statusFilter)}
              onChange={handleStatusFilterChange}
              isClearable={false} // Set to true if you want a clear 'x' button
              placeholder="Select Status..."
              className="rm-react-select-container"
              classNamePrefix="rm-react-select"
              styles={{ container: (base) => ({ ...base, width: '180px' }) }} // Adjust width as needed
            />
          </div>

          <button className="rm-filter-reset" onClick={resetFilters}>
            Reset
          </button>
        </div>
      </div>

      {loading && (
        <div className="rm-loading-state">
          <FaSpinner className="rm-spinner" />
          <span>Loading payouts...</span>
        </div>
      )}

      {error && (
        <div className="rm-error-state">
          <FaExclamationTriangle className="rm-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && payouts.length === 0 && (
        <div className="rm-no-data">No payouts found.</div>
      )}

      {!loading && !error && payouts.length > 0 && (
        <>
          <div className="rm-list-table-container">
            <table className="rm-list-table">
              <thead>
                <tr>
                  <th>DSO</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Month</th>
                  <th>Created On</th>
                  <th>Invoiced On</th>
                  <th>Disbursed On</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout.id}>
                    <td>
                      <Link 
                        to={`/rm/owners/${payout.payment_requester_id}/detail`}
                        className="rm-dso-link"
                      >
                        {payout.payment_requester_name}
                      </Link>
                    </td>
                    <td>{formatCurrency(payout.total_amount)}</td>
                    <td>{renderStatusBadge(payout.status_str)}</td>
                    <td>{formatDateExtractMonth(payout.requested_at)}</td>
                    <td>{formatDate(payout.requested_at)}</td>
                    <td>{formatDate(payout.approved_at)}</td>
                    <td>{formatDate(payout.disbursed_at)}</td>
                    <td>
                      <Link 
                        to={`/rm/payouts/${payout.id}/details`}
                        className="rm-action-button"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rm-pagination-container">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsCount={totalPayouts}
              itemsPerPage={entriesPerPage}
            />
          </div>
        </>
      )}

      <style jsx>{`
        .rm-list-page {
          padding: 15px;
          background-color: #f8fafc;
        }
        
        .rm-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .rm-list-header h2 {
          margin: 0;
          font-size: 1.3rem;
          color: #1e293b;
        }
        
        /* Filters */
        .rm-filters-container {
          background: #fff;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 15px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .rm-filters-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          align-items: center;
        }
        
        .rm-filter {
          display: flex;
          align-items: center;
          gap: 5px;
        }
        
        .rm-filter label {
          font-size: 0.8rem;
          font-weight: 500;
          color: #64748b;
          white-space: nowrap;
        }
        
        .rm-filter select {
          padding: 6px 8px;
          border-radius: 4px;
          border: 1px solid #cbd5e1;
          font-size: 0.85rem;
          max-width: 150px;
        }
        
        .rm-filter-reset {
          margin-left: auto;
          padding: 6px 10px;
          border: none;
          background-color: #e2e8f0;
          color: #475569;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
        }
        
        .rm-filter-reset:hover {
          background-color: #cbd5e1;
        }
        
        /* Table */
        .rm-list-table-container {
          background-color: #ffffff;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          overflow-x: auto;
        }
        
        .rm-list-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        
        .rm-list-table th {
          text-align: left;
          padding: 10px 12px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }
        
        .rm-list-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: middle;
          white-space: nowrap;
        }
        
        .rm-list-table tr:last-child td {
          border-bottom: none;
        }
        
        .rm-list-table tr:hover {
          background-color: #f8fafc;
        }
        
        /* Status Badges */
        .rm-status-badge {
          display: inline-block;
          padding: 4px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 0.25rem;
          text-transform: capitalize;
        }
        
        .rm-status-verified { background-color: #10b981; color: white; }
        .rm-status-active { background-color: #3b82f6; color: white; }
        .rm-status-pending { background-color: #f59e0b; color: white; }
        .rm-status-unknown { background-color: #94a3b8; color: white; }
        
        /* DSO Link style */
        .rm-dso-link {
          color: #3b82f6;
          text-decoration: none;
          font-weight: 500;
        }
        
        .rm-dso-link:hover {
          text-decoration: underline;
          color: #2563eb;
        }
        
        /* Action button style */
        .rm-action-button {
          display: inline-block;
          padding: 5px 10px;
          background-color: #3b82f6;
          color: white;
          border-radius: 4px;
          font-size: 0.75rem;
          text-decoration: none;
          text-align: center;
          transition: background-color 0.2s;
        }
        
        .rm-action-button:hover {
          background-color: #2563eb;
          color: white;
          text-decoration: none;
        }
        
        /* Loading / Error / No Data States */
        .rm-loading-state,
        .rm-error-state,
        .rm-no-data {
          padding: 30px 0;
          text-align: center;
          color: #64748b;
          font-size: 0.9rem;
          background-color: #fff;
          border-radius: 6px;
          margin-top: 15px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        
        .rm-loading-state,
        .rm-error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }
        
        .rm-spinner {
          font-size: 1.5rem;
          color: #6366f1;
          animation: spin 1s linear infinite;
        }
        
        .rm-error-icon {
          font-size: 1.5rem;
          color: #ef4444;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .rm-pagination-container {
          margin-top: 15px;
        }

        /* Styles for react-select */
        .rm-react-select-container {
          /* Adjust width as needed, or use flex-grow if in a flex container */
          /* width: 200px; */ /* Width is set inline via styles prop for now */
          font-size: 0.85rem; /* Match other inputs */
        }
        /* You can target internal parts using the classNamePrefix */
        .rm-react-select__control {
          min-height: 34px; /* Adjust height to match select */
          height: 34px;
          border: 1px solid #cbd5e1; /* Match select border */
          border-radius: 4px; /* Match select border-radius */
          box-shadow: none; /* Remove default focus shadow if needed */
        }
        .rm-react-select__control--is-focused {
           border-color: #a5b4fc; /* Optional: focus style */
           box-shadow: 0 0 0 1px #a5b4fc;
        }
        .rm-react-select__value-container {
            padding: 0 8px; /* Adjust padding */
            height: 34px;
        }
        .rm-react-select__input-container {
            margin: 0;
            padding: 0;
            height: 32px;
        }
        .rm-react-select__indicators {
            height: 32px;
        }
        .rm-react-select__indicator-separator {
            display: none; /* Hide the separator */
        }
        .rm-react-select__menu {
          z-index: 5; /* Ensure dropdown appears above other elements */
          font-size: 0.85rem;
        }
        .rm-react-select__placeholder {
          color: #94a3b8; /* Lighter placeholder text */
        }
        
        /* Create payout button */
        .rm-create-payout-btn {
          background-color: #3b82f6; 
          color: white;
          border: none;
          border-radius: 4px;
          padding: 8px 16px;
          font-size: 0.875rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: background-color 0.2s;
        }
        
        .rm-create-payout-btn:hover {
          background-color: #2563eb;
        }
        
        .btn-icon {
          margin-right: 8px;
          font-size: 0.75rem;
        }

      `}</style>
    </div>
  );
}

export default RMPayouts;
